export type PermissionGrant = {
  appObjectId: string;
  appId: string;
  permissions: string[];
  permissionsGrantedAt?: string | null;
  permissionsUpdatedAt?: string | null;
  permissionsRevokedAt?: string | null;
  revoked: boolean;
};

export type PermissionApplication = {
  appObjectId: string;
  appId: string;
  name: string;
  urlImage?: string;
  urlSite?: string;
  urlCallback?: string;
  allowedPermissions: string[];
};

export type ConsentDetails = {
  application: PermissionApplication;
  grant: PermissionGrant;
  requestedPermissions: string[];
  returnUrl?: string | null;
  countries?: ConsentCountry[];
};

export type ConsentCountry = {
  code: string;
  name: string;
  flag?: string;
};

export type ConsentProfileState = {
  missingFields: string[];
  residenceCountry: string;
};

export type ApplicationPermissionView = PermissionApplication & PermissionGrant;

export const mergeApplicationsAndGrants = (
  applications: PermissionApplication[],
  grants: PermissionGrant[],
  ownAppId: string
): ApplicationPermissionView[] => {
  const applicationsByObjectId = new Map(
    applications.map((application) => [application.appObjectId, application])
  );
  const applicationsByAppId = new Map(
    applications.map((application) => [application.appId, application])
  );

  return grants
    .map((grant) => {
      // Dictionary records may be re-imported with a new MongoDB ObjectId.
      // appId is the stable identifier and keeps an existing consent visible
      // until API-PD canonicalizes it on the next update.
      const application = applicationsByObjectId.get(grant.appObjectId)
        || applicationsByAppId.get(grant.appId);
      if (!application || application.appId === ownAppId) return null;
      return { ...application, ...grant, appId: application.appId };
    })
    .filter((item): item is ApplicationPermissionView => Boolean(item))
    .sort((left, right) => left.name.localeCompare(right.name));
};

export const permissionFieldKey = (path: string): string => path.split(".").pop() || path;

// emails' sub-type keys (primary/work/personal/backup -- see emailFields in
// PersonalData.tsx) are ambiguous on their own: a permission named just
// "Первичный"/"Primary" on the consent screen doesn't say what it's primary
// OF. Qualify with the parent category noun whenever the permission
// addresses one entry of a multi-value category, everywhere a permission
// name (not a raw input field label) is rendered.
const AMBIGUOUS_SUBFIELD_CATEGORIES = new Set(["emails", "phones"]);

export const permissionDisplayLabel = (
  path: string,
  // react-i18next's real TFunction can return a TFunctionDetailedResult
  // object (not just string) depending on call-site generics -- accept
  // `any` here rather than fight that type, same as every other t() call
  // in this codebase already does when interpolated into a template
  // literal.
  t: (key: string, opts?: Record<string, any>) => any
): string => {
  const segments = path.split(".");
  const category = segments[1];
  const field = permissionFieldKey(path);
  const fieldLabel = String(t(`personalData.fields.${field}`, { defaultValue: path }));
  if (segments.length > 2 && AMBIGUOUS_SUBFIELD_CATEGORIES.has(category)) {
    const categoryLabel = String(t(`personalData.fields.${category}`, { defaultValue: category }));
    return `${categoryLabel}: ${fieldLabel}`;
  }
  return fieldLabel;
};

// Derived permissions have no stored value of their own -- they resolve a
// computed result from one or more raw fields that must still be collected
// on the consent page if missing, even though the requesting app never sees
// them and is never granted them. See
// api-pd.id.express/docs/Service_Personal_Data_API.md, sections 12-13.
// ageVerification = legal adult in the user's OWN jurisdiction (needs
// birthDate + placeOfResidence to resolve the threshold).
// minimumAgeVerification = a FIXED age threshold the same for everyone
// (needs only birthDate -- no jurisdiction lookup, so no placeOfResidence).
// These are distinct consent grants; do not merge their required fields.
export const DERIVED_PERMISSION_FIELDS: Record<string, string[]> = {
  "data.derived.ageVerification": ["data.profile.birthDate", "data.profile.placeOfResidence"],
  "data.derived.minimumAgeVerification": ["data.profile.birthDate"],
};

export const isDerivedPermission = (permission: string): boolean =>
  permission.startsWith("data.derived.");

// The raw personal-data fields the consent page must know the presence of:
// every directly requested field plus the fields behind each derived
// permission. Derived keys themselves are never queried -- they have no
// stored value, so API-PD would always report them as "missing" and the
// page would render a meaningless editable input for them.
export const consentFieldsToQuery = (requestedPermissions: string[]): string[] => {
  const fields = new Set<string>();
  requestedPermissions.forEach((permission) => {
    if (isDerivedPermission(permission)) {
      (DERIVED_PERMISSION_FIELDS[permission] || []).forEach((field) => fields.add(field));
    } else {
      fields.add(permission);
    }
  });
  return Array.from(fields);
};

export const normalizeConsentMissingFields = (
  payload: Record<string, any> | null | undefined,
  queriedFields: string[]
): string[] => {
  const queried = new Set(queriedFields.filter((field) => !isDerivedPermission(field)));
  const missingFields: unknown[] = Array.isArray(payload?.missingFields)
    ? payload?.missingFields || []
    : queriedFields;

  return missingFields.filter(
    (field): field is string => typeof field === "string" && queried.has(field)
  );
};

export const buildConsentPersonalDataPayload = (
  values: Record<string, string>,
  permissions: string[],
  residenceCountry = ""
): Record<string, any> => {
  const payload: Record<string, any> = {};

  const countryPath = "data.residence.country";
  const regionPath = "data.residence.region";
  const missingResidence = permissions.some((item) => item === countryPath || item === regionPath);

  permissions.forEach((permission) => {
    if (permission === countryPath || permission === regionPath) return;
    const value = String(values[permission] || "").trim();
    const parts = permission.split(".");
    if (!value || parts[0] !== "data" || parts.length < 3) return;

    let target = payload;
    parts.forEach((part, index) => {
      if (index === parts.length - 1) {
        target[part] = value;
        return;
      }
      target[part] = target[part] || {};
      target = target[part];
    });
  });

  if (missingResidence) {
    const country = String(values[countryPath] || residenceCountry || "").trim().toUpperCase();
    const region = String(values[regionPath] || "").trim().toUpperCase();
    payload.data = payload.data || {};
    payload.data.profile = payload.data.profile || {};
    payload.data.profile.placeOfResidence = region || country;
  }

  return payload;
};
